import { z } from 'zod';
import { passwordSchema, uuidSchema } from '../primitives.js';
import { PERMISSIONS, type Permission } from '../permissions.js';

/**
 * Location logins: a counter signing in as itself.
 *
 * Settings → Locations → [Location] → Login & Access. The owner creates the
 * credential by hand — there is no invitation, no verification email and no
 * self-service password reset, because there is no mailbox and no person on the
 * other end. Everything about the account is the owner's to set and to revoke.
 *
 * The one thing the owner cannot do is move it. A location account is bound to
 * its location for the life of the account; see `business_members.bound_branch_id`.
 */

/**
 * What the owner types. Not an email, and not the account's identity.
 *
 * Deliberately narrow: this gets written on a wall behind a counter and typed
 * by whoever is standing there, so no spaces, no case to remember and no
 * punctuation to mistype. Unique within the business — another company's
 * `holyghost` is a different account entirely.
 */
export const locationUsernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'At least 3 characters')
  .max(32, 'At most 32 characters')
  .regex(
    /^[a-z0-9][a-z0-9._-]*[a-z0-9]$/,
    'Letters, numbers, dots, dashes and underscores — starting and ending with a letter or number',
  );

export const createLocationLoginSchema = z.object({
  username: locationUsernameSchema,
  /** Set by the owner, by hand. Hashed by the same code that hashes a person's. */
  password: passwordSchema,
  /** Shown in the app bar and against anything this login records. */
  displayName: z.string().trim().min(1).max(80).optional(),
  /**
   * Extra permissions on top of the role, filtered by what the business
   * actually runs. See `locationOperatorPermissions`.
   */
  permissions: z.array(z.enum(PERMISSIONS)).max(40).optional(),
  /**
   * Allow Sales Till Access. Owner only, off unless set. Takes effect only
   * while the business runs Sales. See LOCATION_TILL_PERMISSIONS.
   */
  tillAccess: z.boolean().optional(),
});
export type CreateLocationLoginInput = z.infer<typeof createLocationLoginSchema>;

export const updateLocationLoginSchema = z.object({
  username: locationUsernameSchema.optional(),
  displayName: z.string().trim().min(1).max(80).optional(),
  permissions: z.array(z.enum(PERMISSIONS)).max(40).optional(),
  /** Allow Sales Till Access. Owner only. Omitted leaves it as it is. */
  tillAccess: z.boolean().optional(),
  isActive: z.boolean().optional(),
});
export type UpdateLocationLoginInput = z.infer<typeof updateLocationLoginSchema>;

export const resetLocationPasswordSchema = z.object({ password: passwordSchema });
export type ResetLocationPasswordInput = z.infer<typeof resetLocationPasswordSchema>;

export const locationLoginSchema = z.object({
  /** The membership id. Stable, and what the management routes address. */
  id: uuidSchema,
  /** The user id. Immutable, and what every audit row references. */
  userId: uuidSchema,
  branchId: uuidSchema,
  branchName: z.string(),
  username: z.string(),
  displayName: z.string(),
  isActive: z.boolean(),
  /** Null until it has signed in once. */
  lastLoginAt: z.string().nullable(),
  /** The link to write on the wall. Carries the business code and the username. */
  loginUrl: z.string(),
  /** Granted on top of the role, after the module filter. */
  permissions: z.array(z.enum(PERMISSIONS)),
  /** Everything this login can currently do, role plus grants. For the screen. */
  effectivePermissions: z.array(z.enum(PERMISSIONS)),
  /** The owner has allowed Sales Till Access. Live only while Sales is on. */
  tillAccess: z.boolean(),
  createdAt: z.string(),
});
export type LocationLoginView = z.infer<typeof locationLoginSchema>;

/**
 * Signing in as a location.
 *
 * `holyghost` is not globally unique, so the business has to come with it. The
 * copied link supplies the code and prefills the username; a person who knows
 * only the username can still reach the right form through that link.
 */
export const locationSignInSchema = z.object({
  businessCode: z.string().trim().min(4).max(32),
  username: locationUsernameSchema,
  password: z.string().min(1, 'Password is required'),
});
export type LocationSignInInput = z.infer<typeof locationSignInSchema>;

/** What the sign-in page shows before anybody types a password. */
export const locationSignInContextSchema = z.object({
  businessName: z.string(),
  businessCode: z.string(),
});
export type LocationSignInContext = z.infer<typeof locationSignInContextSchema>;

/** Permissions the screen offers, given what this business runs. */
export function grantableForBusiness(
  grantable: readonly Permission[],
  allowed: readonly Permission[],
): Permission[] {
  const set = new Set(allowed);
  return grantable.filter((p) => set.has(p));
}
